import { Body, Toggle, Tooltip } from '@assembly-js/design-system';
import { FormControlLabel, FormGroup } from '@mui/material';
import clsx from 'clsx';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

interface ActivatorProps {
  checked: boolean;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  label?: React.ReactNode;
  name?: string;
  disabled?: boolean;
  isFixedWidth?: boolean;
  tooltip?: string;
  className?: string;
}

interface StyleProps {
  isFixedWidth: boolean;
  label?: React.ReactNode;
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  formGroup: {
    display: 'flex',
    flexDirection: 'inherit',
    justifyContent: 'inherit',
    alignItems: 'inherit',
    position: 'relative',
  },
  formControl: {
    width: props.isFixedWidth ? 200 : 'initial',
    marginLeft: 0,
    marginRight: 0,
    justifyContent: 'space-between',
  },
  label: {
    display: props.label ? 'flex' : 'none',
    marginRight: theme.spacing(2),
    justifyContent: 'space-around',
    '&.Mui-disabled': {
      color: theme.palette.text.primary,
    },
  },
}));

const Activator: React.FC<ActivatorProps> = ({
  checked,
  label,
  name,
  onChange,
  onBlur,
  disabled = false,
  isFixedWidth = true,
  tooltip = '',
  className,
}) => {
  const { classes } = useStyles({ isFixedWidth, label });

  return (
    <Tooltip
      position="bottom"
      content={tooltip ? <Body size="sm">{tooltip}</Body> : null}
      triggerClassname={clsx(classes.formGroup, className)}
    >
      <FormGroup className={clsx(classes.formGroup, className)}>
        <FormControlLabel
          control={
            <Toggle
              label=""
              checked={checked}
              onChange={(e) => {
                const isChecked = e.target.checked;
                onChange(e, isChecked);
              }}
              onBlur={onBlur}
              color={disabled ? 'secondary' : 'default'}
              name={name || 'activator'}
              disabled={disabled}
              data-testid={`${name}Toggle`}
            />
          }
          label={label}
          labelPlacement="start"
          classes={{
            labelPlacementStart: classes.formControl,
            label: `${classes.label}`,
          }}
        />
      </FormGroup>
    </Tooltip>
  );
};

export default Activator;
